import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api, formatApiErrorDetail, getLimitDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { Gift, HandHeart, Sparkles, Loader2, Home } from "lucide-react";
import { LightToggle } from "../components/LightToggle";

const GOLD = "var(--altar-gold)";
const GOLD_FADE = "var(--altar-gold-fade)";
const GOLD_SOFT = "var(--altar-gold-soft)";
const CHARCOAL = "var(--altar-charcoal)";
const MUTED = "var(--altar-muted)";

export default function Well() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { hash } = useLocation();

  useEffect(() => {
    if (hash !== "#well-support") return;
    const t = setTimeout(() => {
      document.getElementById("well-support")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);
    return () => clearTimeout(t);
  }, [hash]);
  const isUser = user && user !== false;

  const [status, setStatus] = useState(null);
  const [theme, setTheme] = useState("");
  const [tradition, setTradition] = useState("");
  const [busy, setBusy] = useState("");
  const [toast, setToast] = useState("");
  const [received, setReceived] = useState(null);
  const [limitNote, setLimitNote] = useState("");

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/well/status");
      setStatus(data);
      if (!theme && data.themes?.length) setTheme(data.themes[0]);
      if (!tradition && data.traditions?.length) setTradition(data.traditions[data.traditions.length - 1]);
    } catch (e) {
      /* silent */
    }
  }, [theme, tradition]);

  useEffect(() => { loadStatus(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2600);
  };

  const handleDonate = async () => {
    if (!isUser) return;
    setBusy("donate");
    setLimitNote("");
    try {
      const { data } = await api.post("/well/donate", { theme, tradition });
      setStatus((s) => ({ ...s, waiting: data.waiting, donated_today: data.donated_today }));
      loadStatus();
      showToast("Your gift now waits in the well for a stranger.");
    } catch (e) {
      const limit = getLimitDetail(e);
      if (limit) setLimitNote(limit.message);
      else showToast(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  };

  const handleReceive = async () => {
    if (!isUser) return;
    setBusy("receive");
    setLimitNote("");
    try {
      const { data } = await api.post("/well/receive");
      setReceived(data);
      loadStatus();
    } catch (e) {
      const limit = getLimitDetail(e);
      if (limit) setLimitNote(limit.message);
      else showToast(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  };

  const handleCheckout = async (packageId) => {
    setBusy(packageId);
    try {
      const { data } = await api.post("/well/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      showToast(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusy("");
    }
  };

  const freeGiftsEnabled = !!status?.free_gifts_enabled;
  const donateLimitReached =
    isUser && status && status.donated_today >= status.donate_limit;
  const noPrayersLeft =
    isUser && status && (status.prayers_remaining_today ?? 1) <= 0;
  const giftRemaining =
    isUser && status
      ? Math.max(0, Math.min(status.donate_limit - status.donated_today, status.prayers_remaining_today ?? 0))
      : 0;
  const receiveLimitReached =
    isUser && status && status.received_today >= status.receive_limit;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // The engine sometimes ends the body with the same closing line; avoid showing it twice.
  const normalizeLine = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const closingIsDuplicated =
    received?.closing &&
    received?.body &&
    normalizeLine(received.body).endsWith(normalizeLine(received.closing));

  return (
    <div className="min-h-screen font-altar p-3 sm:p-5" style={{ backgroundColor: "var(--altar-page)" }} data-testid="well-page">
      <div className="min-h-[calc(100vh-2.5rem)] rounded-md p-1.5" style={{ border: `1.5px solid ${GOLD}` }}>
        <div className="min-h-full rounded-sm" style={{ border: `0.5px solid ${GOLD}`, backgroundColor: "var(--altar-surface)" }}>
          {/* Nav */}
          <nav className="flex items-center justify-between px-6 sm:px-10 py-5" data-testid="well-nav">
            <div className="flex items-center gap-3">
              <Link
                to="/"
                data-testid="nav-home-icon"
                aria-label="Home"
                title="Home"
                className="inline-flex items-center justify-center cursor-pointer transition-opacity hover:opacity-70"
                style={{ color: GOLD }}
              >
                <Home className="w-5 h-5" strokeWidth={1.25} />
              </Link>
              <Link to="/" className="flex items-center gap-2 cursor-pointer" data-testid="well-nav-brand" aria-label="Inlight — home">
                <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: GOLD }} aria-hidden="true" />
              </Link>
            </div>
            <div className="flex items-center gap-5 sm:gap-7 text-base" style={{ color: MUTED }}>
              <LightToggle color={GOLD} />
              {isUser && (
                <>
                  <Link to="/builder" className="hidden sm:inline hover:opacity-70 transition-opacity" data-testid="well-nav-new">
                    New prayer
                  </Link>
                  <Link to="/library" className="hover:opacity-70 transition-opacity" data-testid="well-nav-library">
                    Library
                  </Link>
                </>
              )}
              <Link to="/" className="hover:opacity-70 transition-opacity" data-testid="well-nav-altar">
                ← Inner Light
              </Link>
              {isUser && (
                <button onClick={handleLogout} className="hover:opacity-70 transition-opacity" data-testid="well-nav-logout">
                  Sign out
                </button>
              )}
            </div>
          </nav>
          <div className="mx-6 sm:mx-10" style={{ height: "1px", backgroundColor: GOLD_FADE }} aria-hidden="true" />

          {/* Hero */}
          <header className="altar-fade text-center px-6 pt-14 sm:pt-20 pb-10">
            <p className="text-[13px] tracking-[0.3em] uppercase" style={{ color: MUTED }} data-testid="well-eyebrow">
              — A shared well of prayer —
            </p>
            <h1
              className="font-square uppercase mx-auto mt-7 text-[26px] sm:text-[34px] leading-[1.4] tracking-[0.04em]"
              style={{ color: CHARCOAL, fontWeight: 500, maxWidth: "640px" }}
              data-testid="well-title"
            >
              The Prayer Well
            </h1>
            <p className="mt-6 italic text-[18px] max-w-xl mx-auto" style={{ color: MUTED }} data-testid="well-subtitle">
              Gift a prayer for a stranger. Receive one shaped by an unknown hand. Funded with care, kept anonymous.
            </p>
            <div className="mt-8 inline-flex items-center gap-2.5 px-6 py-2.5 rounded-full bg-white"
              style={{ border: `0.5px solid ${GOLD}`, boxShadow: "0 2px 10px rgba(184,147,90,0.08)" }}
              data-testid="well-counter">
              <span className="text-lg" style={{ color: GOLD_SOFT }} aria-hidden="true">✦</span>
              <span className="text-lg" style={{ color: CHARCOAL, fontWeight: 500 }} data-testid="well-counter-value">
                {status ? status.waiting : "…"}
              </span>
              <span className="text-sm" style={{ color: MUTED }}>
                {status && status.waiting === 1 ? "prayer waiting to be received" : "prayers waiting to be received"}
              </span>
            </div>
          </header>

          {limitNote && (
            <div className="max-w-xl mx-auto px-6 mb-6">
              <p className="text-center italic text-base px-6 py-4 rounded-sm bg-white"
                style={{ color: CHARCOAL, border: `0.5px solid ${GOLD}` }} data-testid="well-limit-note">
                {limitNote}
              </p>
            </div>
          )}

          {/* Two panels */}
          <section className="px-6 sm:px-10 pb-6" data-testid="well-panels">
            <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Gift a prayer */}
              <div className="bg-white rounded-sm p-8" style={{ border: `0.5px solid ${GOLD}`, boxShadow: "0 2px 10px rgba(184,147,90,0.08)" }} data-testid="panel-gift">
                <div className="text-center mb-6">
                  <Gift className="w-6 h-6 mx-auto mb-3" strokeWidth={1.3} style={{ color: GOLD_SOFT }} />
                  <h2 className="font-square uppercase text-lg tracking-[0.06em]" style={{ color: CHARCOAL, fontWeight: 500 }}>Gift a prayer</h2>
                  <p className="mt-2 text-sm italic" style={{ color: MUTED }}>
                    {freeGiftsEnabled
                      ? "Choose a theme and tradition. The engine shapes it; you never see who receives it."
                      : "A prayer shaped by the engine and left for a stranger you never see."}
                  </p>
                </div>

                {!freeGiftsEnabled ? (
                  <div className="text-center py-4" data-testid="gift-funded-only">
                    <p className="text-base italic mb-6" style={{ color: MUTED }}>
                      Gifts to the well are not free — each one is a prayer written and voiced with care.
                      Fund a gift below and we will shape them for you, quietly and anonymously.
                    </p>
                    <a
                      href="#well-support"
                      data-testid="gift-fund-link"
                      className="inline-block px-6 py-2.5 rounded-full text-sm transition"
                      style={{ border: `1px solid ${GOLD}`, color: CHARCOAL }}
                    >
                      Fund a gift of prayers
                    </a>
                  </div>
                ) : isUser ? (
                  <>
                    <p className="text-[11px] tracking-[0.25em] uppercase mb-2.5" style={{ color: MUTED }}>Theme</p>
                    <div className="flex flex-wrap gap-2 mb-5" data-testid="gift-themes">
                      {status?.themes?.map((t) => (
                        <button key={t} onClick={() => setTheme(t)} data-testid={`gift-theme-${t.toLowerCase()}`}
                          className="px-3.5 py-1.5 rounded-full text-sm transition"
                          style={{ backgroundColor: theme === t ? GOLD_SOFT : "white", color: theme === t ? "white" : CHARCOAL, border: `1px solid ${theme === t ? GOLD_SOFT : "var(--altar-line)"}` }}>
                          {t}
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] tracking-[0.25em] uppercase mb-2.5" style={{ color: MUTED }}>Tradition</p>
                    <div className="flex flex-wrap gap-2 mb-6" data-testid="gift-traditions">
                      {status?.traditions?.map((t) => (
                        <button key={t} onClick={() => setTradition(t)} data-testid={`gift-tradition-${t.toLowerCase()}`}
                          className="px-3.5 py-1.5 rounded-full text-sm transition"
                          style={{ backgroundColor: tradition === t ? GOLD_SOFT : "white", color: tradition === t ? "white" : CHARCOAL, border: `1px solid ${tradition === t ? GOLD_SOFT : "var(--altar-line)"}` }}>
                          {t}
                        </button>
                      ))}
                    </div>
                    <button onClick={handleDonate} disabled={busy === "donate" || donateLimitReached || noPrayersLeft}
                      data-testid="gift-submit-button"
                      className="w-full py-3.5 rounded-full text-base transition disabled:opacity-50 inline-flex items-center justify-center gap-2"
                      style={{ backgroundColor: CHARCOAL, color: "var(--altar-surface)" }}>
                      {busy === "donate" ? <><Loader2 className="w-4 h-4 animate-spin" /> Shaping the gift…</>
                        : donateLimitReached ? "You have gifted all of today's prayers"
                        : noPrayersLeft ? "Today's prayers are all used"
                        : <>Gift this prayer <Sparkles className="w-4 h-4" strokeWidth={1.5} /></>}
                    </button>
                    <p className="mt-3 text-center text-sm" style={{ color: MUTED }} data-testid="gift-count">
                      {status
                        ? giftRemaining > 0
                          ? `${status.donated_today} gifted today · ${giftRemaining} more you can give`
                          : `${status.donated_today} gifted today`
                        : ""}
                    </p>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-base italic mb-5" style={{ color: MUTED }}>
                      Sign in to gift a free prayer — or leave a gift of support below.
                    </p>
                    <Link to="/login" data-testid="gift-signin-link"
                      className="inline-block px-6 py-2.5 rounded-full text-sm transition"
                      style={{ border: `1px solid ${GOLD}`, color: CHARCOAL }}>
                      Sign in to gift
                    </Link>
                  </div>
                )}
              </div>

              {/* Receive a prayer */}
              <div className="bg-white rounded-sm p-8" style={{ border: `0.5px solid ${GOLD}`, boxShadow: "0 2px 10px rgba(184,147,90,0.08)" }} data-testid="panel-receive">
                <div className="text-center mb-6">
                  <HandHeart className="w-6 h-6 mx-auto mb-3" strokeWidth={1.3} style={{ color: GOLD_SOFT }} />
                  <h2 className="font-square uppercase text-lg tracking-[0.06em]" style={{ color: CHARCOAL, fontWeight: 500 }}>Receive a prayer</h2>
                  <p className="mt-2 text-sm italic" style={{ color: MUTED }}>
                    Draw a prayer from the well — gifted by someone you'll never meet.
                  </p>
                </div>

                {isUser ? (
                  <div className="text-center pt-2">
                    <button onClick={handleReceive} disabled={busy === "receive" || receiveLimitReached}
                      data-testid="receive-submit-button"
                      className="w-full py-3.5 rounded-full text-base transition disabled:opacity-50 inline-flex items-center justify-center gap-2"
                      style={{ backgroundColor: GOLD_SOFT, color: "white" }}>
                      {busy === "receive" ? <><Loader2 className="w-4 h-4 animate-spin" /> Drawing from the well…</>
                        : receiveLimitReached ? "You have received today's gift"
                        : <>Receive a gifted prayer <HandHeart className="w-4 h-4" strokeWidth={1.5} /></>}
                    </button>
                    <p className="mt-3 text-sm" style={{ color: MUTED }} data-testid="receive-count">
                      {status ? `${status.received_today} of ${status.receive_limit} received today` : ""}
                    </p>
                    <p className="mt-4 text-sm italic" style={{ color: MUTED }}>
                      {status && status.waiting === 0
                        ? "The well is quiet just now — be the first to leave a gift."
                        : "A prayer is waiting for you."}
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-base italic mb-5" style={{ color: MUTED }}>
                      Sign in to receive a gifted prayer from the well.
                    </p>
                    <Link to="/login" data-testid="receive-signin-link"
                      className="inline-block px-6 py-2.5 rounded-full text-sm transition"
                      style={{ border: `1px solid ${GOLD}`, color: CHARCOAL }}>
                      Sign in to receive
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Ornament divider */}
          <div className="text-center py-10" aria-hidden="true">
            <span className="text-sm" style={{ color: GOLD }}>✦</span>
          </div>

          {/* Money gift */}
          <section id="well-support" className="px-6 sm:px-10 pb-16 text-center" data-testid="well-support">
            <p className="text-[13px] tracking-[0.3em] uppercase" style={{ color: MUTED }}>Leave a gift of support</p>
            <p className="mt-4 italic text-base max-w-lg mx-auto" style={{ color: MUTED }}>
              Each gift funds prayers that are quietly shaped and added to the well for strangers to receive.
            </p>
            <div className="max-w-3xl mx-auto mt-9 grid grid-cols-1 sm:grid-cols-3 gap-5" data-testid="support-packages">
              {status?.packages?.map((p) => (
                <button key={p.id} onClick={() => handleCheckout(p.id)} disabled={busy === p.id}
                  data-testid={`support-package-${p.id}`}
                  className="bg-white rounded-sm py-9 px-4 transition-shadow hover:shadow-md disabled:opacity-60"
                  style={{ border: `0.5px solid ${GOLD}`, boxShadow: "0 2px 10px rgba(184,147,90,0.08)" }}>
                  <p className="font-square text-3xl" style={{ color: CHARCOAL, fontWeight: 500 }}>${p.amount_usd}</p>
                  <p className="mt-3 text-base" style={{ color: GOLD_SOFT }}>
                    {busy === p.id ? "Opening…" : `funds ${p.prayers} prayers`}
                  </p>
                </button>
              ))}
            </div>
            <p className="mt-8 text-xs tracking-[0.15em] uppercase" style={{ color: MUTED }}>
              Payments handled securely by Stripe · Anonymous
            </p>
          </section>

          {/* Footer */}
          <footer className="px-6 sm:px-10 pb-10 text-center" data-testid="well-footer">
            <div className="mb-6" style={{ height: "1px", backgroundColor: GOLD_FADE }} aria-hidden="true" />
            <p className="text-2xl tracking-[0.04em]" style={{ color: CHARCOAL, fontWeight: 500 }} data-testid="well-brand-tagline">
              Inlight
            </p>
            <p className="mt-2 italic text-sm" style={{ color: MUTED }}>
              A quiet place for prayer.
            </p>
          </footer>
        </div>
      </div>

      {/* Received prayer dialog */}
      <Dialog open={!!received} onOpenChange={(o) => !o && setReceived(null)}>
        <DialogContent className="max-w-lg border-0 p-0 bg-transparent shadow-none" data-testid="received-prayer-dialog">
          <DialogTitle className="sr-only">A gift from the well</DialogTitle>
          <div className="bg-white rounded-sm px-8 py-10 text-center max-h-[85vh] overflow-y-auto" style={{ border: `1px solid ${GOLD}`, boxShadow: "0 12px 40px rgba(45,44,42,0.18)" }}>
            <p className="text-[11px] tracking-[0.3em] uppercase mb-5" style={{ color: GOLD_SOFT }} data-testid="received-eyebrow">
              A gift from the well
            </p>
            {received && (
              <>
                <p className="text-sm italic mb-5" style={{ color: MUTED }} data-testid="received-origin-label">
                  {received.tradition === "Universal"
                    ? `A universal prayer of ${(received.theme || "peace").toLowerCase()}, gifted for you.`
                    : `A ${received.tradition} prayer of ${(received.theme || "peace").toLowerCase()}, gifted for you.`}
                </p>
                <h3 className="font-serif-display text-2xl leading-snug mb-6" style={{ color: CHARCOAL }} data-testid="received-title">
                  {received.title}
                </h3>
                <div className="font-serif-display italic text-lg leading-loose whitespace-pre-wrap" style={{ color: CHARCOAL }} data-testid="received-body">
                  {received.body}
                </div>
                {received.closing && !closingIsDuplicated && (
                  <p className="mt-6 font-serif-display text-base" style={{ color: GOLD_SOFT }} data-testid="received-closing">
                    {received.closing}
                  </p>
                )}
                <button onClick={() => setReceived(null)} data-testid="received-close-button"
                  className="mt-9 px-8 py-2.5 rounded-full text-sm transition"
                  style={{ border: `1px solid ${GOLD}`, color: CHARCOAL }}>
                  Keep it with me
                </button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {toast && (
        <div className="fixed bottom-8 inset-x-0 flex justify-center pointer-events-none z-50" data-testid="well-toast">
          <div className="px-5 py-2.5 rounded-full text-sm" style={{ backgroundColor: CHARCOAL, color: "var(--altar-surface)" }}>{toast}</div>
        </div>
      )}
    </div>
  );
}
